import React, { useState, useEffect, useLayoutEffect, useReducer, useRef, useContext } from "react";
import { useParams } from "react-router-dom";

import { isSameDay, parseISO, format, isToday, isYesterday } from "date-fns";
import clsx from "clsx";

import { green, blue } from "@material-ui/core/colors";
import {
  Avatar,
  Button,
  IconButton,
  makeStyles,
  Typography,
  Tooltip,
} from "@material-ui/core";

import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ErrorOutline,
  ExpandMore,
  GetApp,
  Description,
  Reply,
  PlayArrow,
  Pause,
  Mic,
  Person,
  KeyboardArrowDown,
  EmojiEmotions,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import MessagesListSkeleton from "../MessagesListSkeleton";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { detectTextDirection } from "../../helpers/detectTextDirection";

import api from "../../services/api";
import * as apiVoice from "../../services/apiVoice";
import VoiceCallTimelineItem from "../VoiceCallTimelineItem";
import ErrorBoundary from "../ErrorBoundary";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";
import vCard from "vcard-parser";
import { generateColor } from "../../helpers/colorGenerator";
import { getInitials } from "../../helpers/getInitials";
import { Mutex } from "async-mutex";
import { Checkbox } from "@material-ui/core";

const loadPageMutex = new Mutex();

// Matches an auto-generated media FILE NAME passed through as a message body
// (e.g. "IMG_1234.jpg", "audio-record-site-9.mp3") so it can be hidden instead
// of shown as text/caption. Restricted to REAL media/file extensions: the
// earlier /^[\w.\-]+\.[a-z0-9]{1,8}$/i also matched ordinary customer text
// shaped like "<token>.<1-8 chars>" — date ranges ("14-18.09"), times
// ("15.30"), prices ("10.5") — and silently hid them (only the timestamp
// rendered). Shared by the text-body render and the image/video/document
// caption checks so the rule can never diverge between them. (Ticket #11436.)
const AUTO_FILENAME_RE =
  /^[\w.\-]+\.(jpe?g|png|gif|bmp|tiff?|heic|heif|webp|svg|ico|mp4|webm|mov|avi|mkv|3gp|m4v|wmv|mpe?g|mp3|ogg|oga|wav|m4a|aac|opus|flac|wma|amr|pdf|docx?|xlsx?|pptx?|txt|csv|zip|rar|7z|json)$/i;

const useStyles = makeStyles((theme) => ({
  messageContainer: {
    "& a": {
      color: theme.palette.primary.main,
      fontWeight: "bold",
      textDecoration: "none",
    },    
  },
  
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
    // Phones routinely run 320–390 css px; the 300 floor still fits.
    // The bubble children below also clamp themselves to the parent
    // content box on xs so an image-heavy message can't blow the
    // chat past the viewport edge.
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
  },

  messagesList: {
    // Chat wallpaper. Light: warm tan with a vertical wash so bubbles
    // float instead of sitting on a flat panel. Dark: deep brand
    // surface (#0F1210) — the same Tasks/Settings dark token — with a
    // brand-tinted radial in the corner for depth.
    background:
      theme.palette.type === "dark"
        ? "radial-gradient(circle at 0% 0%, rgba(46,227,122,0.05) 0%, transparent 50%), #0F1210"
        : "linear-gradient(180deg, #efeae2 0%, #e6e1d4 100%)",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    // Horizontal padding tightened from 20px → 12px so the 95%-max-width
    // bubbles get more usable canvas. The previous 20px gutter left a
    // visible empty band on the side of the chat that read as the chat
    // not being stretched to the box edge.
    padding: "20px 12px",
    overflowY: "scroll",
    // Hitting top/bottom of the chat must NOT chain into the page.
    overscrollBehavior: "contain",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderRadius: 0,
    // RTL support - this makes flex-start/flex-end respect text direction
    direction: theme.direction,
    [theme.breakpoints.down("xs")]: {
      padding: "12px 8px",
    },
  },

  messageLeft: {
    marginRight: theme.direction === "rtl" ? 0 : 20,
    marginLeft: theme.direction === "rtl" ? 20 : 0,
    marginTop: 2,
    marginBottom: 8,
    minWidth: 100,
    maxWidth: "95%",
    // overflow:hidden REMOVED — Sprint 8 final review (Amit 2026-06-08).
    // Was Sprint 7's clip for the legacy ExpandMore icon (top:0 right:0).
    // Sprint 8A replaced that icon; overflow:hidden + Sprint 5 padding
    // was the two-factor collapse bug. Stays removed.
    // Mobile: tighten the trailing-edge margin and clamp to the
    // chat container's content box. Without this the 20 px sibling
    // margin + 85% width could still let a bubble overflow on
    // narrow phones once you account for the bubble's own padding.
    // minWidth at 220 keeps image thumbnails legible — they otherwise
    // shrink to the source thumb's intrinsic px (often 100 px) on a
    // block element with no explicit width, looking broken.
    [theme.breakpoints.down("xs")]: {
      marginRight: theme.direction === "rtl" ? 0 : 8,
      marginLeft: theme.direction === "rtl" ? 8 : 0,
      maxWidth: "calc(100% - 16px)",
      // Floor the width so the bottom-right timestamp always fits INSIDE the
      // bubble. The timestamp is position:absolute → it is out of flow, so a
      // width:fit-content bubble sizes to the text alone and ignores it; an
      // "OK" bubble was narrower than "14:05" and the time spilled past the
      // edge (user report 2026-06-29). 80px holds the incoming time without
      // re-introducing the old 220px near-edge-to-edge stretch. Bubbles still
      // shrink to content above the floor; image-bearing bubbles get their own
      // minWidth via the modifier on the media nodes themselves.
      minWidth: 80,
      width: "fit-content",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
    },
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: theme.direction === "rtl" ? "auto" : 0,
      left: theme.direction === "rtl" ? 0 : "auto",
    },
    // Sprint 8A — hover-reveal the multi-action row on incoming bubbles.
    // Anchored to the trailing edge of the bubble (LTR right / RTL left)
    // so it doesn't overlap the leading-edge content. Uses JSS $ref to
    // the messageActionsRow class — id-based descendant selectors were
    // duplicating an id on every bubble in the list (HTML violation).
    "&:hover $messageActionsRow": {
      display: "flex",
      right: theme.direction === "rtl" ? "auto" : 4,
      left: theme.direction === "rtl" ? 4 : "auto",
    },

    whiteSpace: "pre-wrap",
    // Incoming bubble: clean surface tone. Dark uses a brand-neutral
    // dark card with a hairline border so it reads against the
    // wallpaper without competing with outgoing bubbles.
    backgroundColor:
      theme.palette.type === "dark" ? "#1A1F1C" : "#ffffff",
    color:
      theme.palette.type === "dark" ? "#e6f7ec" : "#102b1f",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.05)"
        : "1px solid rgba(15,23,42,0.04)",
    alignSelf: "flex-start",
    // Tail on the leading edge so the bubble "points" at the sender
    // gutter. RTL flips which corner stays sharp.
    borderTopLeftRadius: theme.direction === "rtl" ? 12 : 2,
    borderTopRightRadius: theme.direction === "rtl" ? 2 : 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 2px rgba(0,0,0,0.35)"
        : "0 1px 2px rgba(15,23,42,0.06), 0 2px 6px rgba(15,23,42,0.03)",
    transition:
      "background-color 0.2s ease, box-shadow 0.15s ease, transform 0.15s ease",
    textAlign: theme.direction === "rtl" ? "right" : "left",
    direction: theme.direction,
    "&:hover": {
      boxShadow:
        theme.palette.type === "dark"
          ? "0 2px 6px rgba(0,0,0,0.45)"
          : "0 2px 6px rgba(15,23,42,0.10), 0 4px 12px rgba(15,23,42,0.05)",
    },
  },

  quotedContainerLeft: {
    // Sprint 5 changed bubble padding from "3px 80px 6px 6px" to
    // "3px 6px 20px 6px". The old -80px right margin was designed to
    // cancel out the 80px right-padding so the quoted block stretched
    // edge-to-edge. With only 6px right-padding now, -80px pushes the
    // quoted block 74px OUTSIDE the bubble — fix: use -6px everywhere.
    margin: "-3px -6px 6px -6px",
    [theme.breakpoints.down("xs")]: {
      margin: "-3px -6px 6px -6px",
    },
    overflow: "hidden",
    backgroundColor: theme.palette.type === 'light' ? "#f0f0f0" : "#1c2134",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    cursor: "pointer",
  },

  quotedMsg: {
    padding: 10,
    // maxWidth: 300,
    width: "100%",
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  quotedThumbnail: {
    maxWidth: "74px",
    maxHeight: "74px",
  },

  // ── Sprint 9A (Gal 2026-06-08): unified reply-preview layout ──
  // All quoted-message replies render in the same 60px-high pill
  // regardless of media kind. The previous (sprint 8 fix) version
  // used the same quoted* classes as renderLinkPreview, which
  // forced both renderers to share a layout — so changing one
  // broke the other. New replyPreview* family is dedicated to
  // renderQuotedMessage and leaves renderLinkPreview untouched.
  //
  //   ┌────────────────────────────────────────────┐ 60px
  //   │ ▌ Sender name (ellipsis)                   │
  //   │ ▌ 📷 caption text (1 line, ellipsis)       │  thumbnail
  //   │ ▌                                          │   40x40 →
  //   └────────────────────────────────────────────┘
  replyPreviewContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    height: 60,
    margin: "-3px -6px 6px -6px",
    [theme.breakpoints.down("xs")]: {
      margin: "-3px -6px 6px -6px",
    },
    overflow: "hidden",
    borderRadius: "7.5px",
    cursor: "pointer",
    backgroundColor: theme.palette.type === "dark" ? "#1c2134" : "#f0f0f0",
  },
  replyPreviewContainerOut: {
    backgroundColor: theme.palette.type === "dark" ? "#075e54" : "#cfe9ba",
  },
  replyPreviewBar: {
    flex: "none",
    width: 4,
    backgroundColor: "#6bcbef",
  },
  replyPreviewBarOut: {
    backgroundColor: "#35cd96",
  },
  replyPreviewText: {
    flex: 1,
    minWidth: 0,
    padding: "8px 10px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    overflow: "hidden",
  },
  replyPreviewName: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "#6bcbef",
    lineHeight: 1.3,
    marginBottom: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  replyPreviewBody: {
    fontSize: 13,
    lineHeight: 1.35,
    color:
      theme.palette.type === "dark"
        ? "rgba(230,247,236,0.85)"
        : "rgba(15,23,42,0.72)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  replyPreviewThumb: {
    position: "relative",
    flexShrink: 0,
    width: 40,
    height: 40,
    alignSelf: "center",
    margin: theme.direction === "rtl" ? "0 0 0 10px" : "0 10px 0 0",
    borderRadius: 6,
    overflow: "hidden",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    },
  },
  replyPreviewPlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    "& svg": {
      fontSize: 16,
    },
  },

  messageRight: {
    marginLeft: theme.direction === "rtl" ? 0 : 20,
    marginRight: theme.direction === "rtl" ? 20 : 0,
    marginTop: 2,
    marginBottom: 8,
    minWidth: 100,
    maxWidth: "95%",
    // overflow:hidden REMOVED — see messageLeft comment above.
    // Same reasoning: Sprint 8A action row stays inside the bubble.
    // Same mobile clamp as messageLeft — see comment above.
    [theme.breakpoints.down("xs")]: {
      marginLeft: theme.direction === "rtl" ? 0 : 8,
      marginRight: theme.direction === "rtl" ? 8 : 0,
      maxWidth: "calc(100% - 16px)",
      // See messageLeft xs override. Floor wider here: the OUTGOING timestamp
      // also carries the ack check-marks (renderMessageAck) and an optional
      // "edited" label, so it is wider than the incoming time — 110px keeps it
      // inside the bubble for short outgoing messages without the old 220px
      // stretch.
      minWidth: 110,
      width: "fit-content",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
    },
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: theme.direction === "rtl" ? "auto" : 0,
      left: theme.direction === "rtl" ? 0 : "auto",
    },
    // Sprint 8A — hover action row on outgoing bubbles, mirrored from
    // messageLeft. Trailing edge in LTR = right; in RTL = left.
    "&:hover $messageActionsRow": {
      display: "flex",
      right: theme.direction === "rtl" ? "auto" : 4,
      left: theme.direction === "rtl" ? 4 : "auto",
    },
    whiteSpace: "pre-wrap",
    // Outgoing bubble carries the brand identity — soft brand-green
    // tint in light mode (kept legible against dark text), deeper
    // brand-green gradient in dark mode with white text. Ambient
    // shadow gives it lift over the wallpaper without a hard outline.
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, #1f7d65 0%, #16604f 100%)"
        : "linear-gradient(135deg, #d8f5c1 0%, #c5edaa 100%)",
    color:
      theme.palette.type === "dark" ? "#e6f7ec" : "#0c2a1f",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(46,227,122,0.18)"
        : "1px solid rgba(37,211,102,0.18)",
    alignSelf: "flex-end",
    // Tail on the trailing edge — keeps the bubble visually anchored
    // to the operator's side of the chat.
    borderTopLeftRadius: 12,
    borderTopRightRadius: theme.direction === "rtl" ? 2 : 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: theme.direction === "rtl" ? 12 : 2,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 2px rgba(0,0,0,0.4), 0 0 0 1px rgba(46,227,122,0.06)"
        : "0 1px 2px rgba(15,23,42,0.06), 0 2px 8px rgba(37,211,102,0.10)",
    transition:
      "background-color 0.2s ease, box-shadow 0.15s ease",
    textAlign: theme.direction === "rtl" ? "right" : "left",
    direction: theme.direction,
    "&:hover": {
      boxShadow:
        theme.palette.type === "dark"
          ? "0 2px 8px rgba(0,0,0,0.5), 0 0 0 1px rgba(46,227,122,0.12)"
          : "0 2px 8px rgba(15,23,42,0.10), 0 4px 14px rgba(37,211,102,0.18)",
    },
  },

  quotedContainerRight: {
    // Same fix as quotedContainerLeft — -80px was for the old 80px
    // right-padding; Sprint 5 reduced it to 6px so we match.
    margin: "-3px -6px 6px -6px",
    [theme.breakpoints.down("xs")]: {
      margin: "-3px -6px 6px -6px",
    },
    overflowY: "hidden",
    backgroundColor: theme.palette.type === 'light' ? "#cfe9ba" : "#075e54",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    // maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  // Sprint 8A — hover action row. Holds the three quick-action buttons
  // (😊 reactions, ↩️ reply, ⌄ full menu). display:none by default so
  // the bubble has no chrome at rest; the `&:hover #messageActionsRow`
  // rule on messageLeft/messageRight flips it to flex on hover. The row
  // sits inside the bubble's relative box and is itself absolute, so it
  // never displaces the message content — bubble layout is identical
  // whether the row is visible or not.
  messageActionsRow: {
    display: "none",
    position: "absolute",
    top: 2,
    zIndex: 2,
    background:
      theme.palette.type === "dark"
        ? "rgba(26,31,28,0.92)"
        : "rgba(255,255,255,0.92)",
    borderRadius: 14,
    padding: "1px 2px",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 4px rgba(0,0,0,0.45)"
        : "0 1px 4px rgba(15,23,42,0.12)",
    backdropFilter: "blur(2px)",
    pointerEvents: "auto",
  },

  quickActionButton: {
    padding: 3,
    color: theme.palette.type === "dark" ? "#cfe9d8" : "#5f6f6a",
    "& svg": { fontSize: 18 },
    "&:hover": {
      color: theme.palette.type === "dark" ? "#2ee37a" : "#128c7e",
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(46,227,122,0.08)"
          : "rgba(18,140,126,0.08)",
    },
  },


  // Sprint 8D — scroll-to-bottom FAB. Absolute-positioned inside the
  // messagesListWrapper so it floats above the scroll area but is
  // contained by the chat panel (not the whole viewport). Opacity
  // transition rather than display:none keeps the button render-stable
  // across show/hide cycles.
  scrollToBottomFab: {
    position: "absolute",
    bottom: 16,
    right: theme.direction === "rtl" ? "auto" : 16,
    left: theme.direction === "rtl" ? 16 : "auto",
    width: 38,
    height: 38,
    minHeight: 38,
    borderRadius: "50%",
    backgroundColor:
      theme.palette.type === "dark" ? "#1A1F1C" : "#ffffff",
    color: theme.palette.type === "dark" ? "#e6f7ec" : "#5f6f6a",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 2px 6px rgba(0,0,0,0.5)"
        : "0 2px 6px rgba(15,23,42,0.18)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.05)"
        : "1px solid rgba(15,23,42,0.06)",
    zIndex: 5,
    opacity: 0,
    transform: "translateY(8px)",
    pointerEvents: "none",
    transition: "opacity 0.15s ease, transform 0.15s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark" ? "#21271f" : "#f8f8f5",
    },
    // Android audit 2026-06-30: 38px is below the 44px touch-target minimum on
    // phones. Bump to 44px on phones only (down('xs') = the 600px cutover this
    // app uses); desktop/tablet ≥600px keeps the compact 38px FAB.
    [theme.breakpoints.down("xs")]: {
      width: 44,
      height: 44,
      minHeight: 44,
    },
  },
  scrollToBottomFabVisible: {
    opacity: 1,
    transform: "translateY(0)",
    pointerEvents: "auto",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    maxWidth: "100%",
    // padding-bottom: 20px reserves space for the absolute-positioned
    // timestamp chip at bottom:2,right:8 so it never overlaps the text.
    padding: "3px 6px 20px 6px",
    position: "relative",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    maxWidth: "100%",
    padding: "3px 6px 20px 6px",
    position: "relative",
  },

  textContentItemEdited: {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    maxWidth: "100%",
    padding: "3px 6px 20px 6px",
    position: "relative",
  },

  messageVideo: {
    width: "100%",
    maxWidth: 320,
    maxHeight: 445,
    // Reserve the box BEFORE metadata/poster resolve. Without an explicit
    // aspect ratio a <video preload="metadata"> paints at the replaced-element
    // default (~300x150) and then grows to its intrinsic size a beat later,
    // reflowing every bubble below it and shifting the open-scroll anchor.
    // Portrait clips letterbox inside the 16:9 box (bg is already #000) —
    // stable geometry beats a post-paint jump.
    aspectRatio: "16 / 9",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    backgroundColor: "#000",
    // Mobile responsive
    [theme.breakpoints.down("sm")]: {
      maxWidth: 280,
    },
  },

  messageMediaSticker: {
    backgroundColor: "unset",
    boxShadow: "unset",
  },

  timestamp: {
    fontSize: 10.5,
    fontWeight: 500,
    position: "absolute",
    bottom: 2,
    right: 8,
    color:
      theme.palette.type === "dark"
        ? "rgba(230,247,236,0.55)"
        : "rgba(15,23,42,0.50)",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    whiteSpace: "nowrap",
    letterSpacing: 0.2,
    marginLeft: 4,
  },

  timestampStickerLeft: {
    backgroundColor: theme.palette.type === 'light' ? "#ffffff" : "#024481",
    borderRadius: 8,
    padding: 5,
    boxShadow: theme.palette.type === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  timestampStickerRight: {
    backgroundColor: theme.palette.type === 'light' ? "#dcf8c6" : "#128c7e",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.palette.type === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    // Static light blue turned into a white chip in dark mode.
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: theme.palette.type === "dark" ? "none" : "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: theme.palette.type === "dark" ? "rgba(230,247,236,0.75)" : "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  forwardedLabel: {
    display: "flex",
    alignItems: "center",
    fontSize: 11,
    color: "#667781",
    fontStyle: "italic",
    marginBottom: 4,
    gap: 4,
  },

  forwardedIcon: {
    fontSize: 14,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackDoneReadIcon: {
    color: blue[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  // Document card — Telegram/WhatsApp style. A round icon chip on the
  // leading edge, filename + size stacked next to it, the entire
  // card is clickable. Replaces the old <Button> with overlapping
  // start/end icons that produced the cramped, broken-looking layout
  // in Raviv's screenshot.
  documentCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    margin: "2px 0",
    borderRadius: 10,
    cursor: "pointer",
    textDecoration: "none",
    color: "inherit",
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.04)"
      : "rgba(0,0,0,0.04)",
    transition: "background 120ms ease",
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.07)",
    },
  },
  documentCardIcon: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.type === "dark"
      ? "rgba(46, 227, 122, 0.16)"
      : "rgba(18, 140, 126, 0.12)",
    color: theme.palette.type === "dark" ? "#2ee37a" : "#128c7e",
    "& svg": { fontSize: 22 },
  },
  documentCardBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  documentCardName: {
    fontSize: 13.5,
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#e6f7ec" : "#102b1f",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.3,
  },
  documentCardMeta: {
    fontSize: 11,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.55)" : "#5f6f6a",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  imageLocation: {
    position: 'relative',
    width: '100%',
    height: 80,
    borderRadius: 5
  },
  textRight: {
    textAlign: "right",
    direction: "rtl"
  },

  '@global': {
    '@keyframes wave': {
      '0%, 60%, 100%': {
        transform: 'initial',
      },
      '30%': {
        transform: 'translateY(-15px)',
      },
    },
    '@keyframes quiet': {
      '25%': {
        transform: 'scaleY(.6)'
      },
      '50%': {
        transform: 'scaleY(.4)',
      },
      '75%': {
        transform: 'scaleY(.8)',
      }
    },
    '@keyframes normal': {
      '25%': {
        transform: 'scaleY(.1)'
      },
      '50%': {
        transform: 'scaleY(.4)',
      },
      '75%': {
        transform: 'scaleY(.6)',
      }
    },
    '@keyframes loud': {
      '25%': {
        transform: 'scaleY(1)'
      },
      '50%': {
        transform: 'scaleY(.4)',
      },
      '75%': {
        transform: 'scaleY(1.2)',
      }
    },
  },
  wave: {
    position: 'relative',
    textAlign: 'center',
    height: "30px",
    marginTop: "10px",
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  dot: {
    display: "inline-block",
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    marginRight: "3px",
    background: theme.palette.type === 'light' ? "#303030" : "#ffffff",
    animation: "wave 1.3s linear infinite",
    "&:nth-child(2)": {
      animationDelay: "-1.1s",
    },
    "&:nth-child(3)": {
      animationDelay: "-0.9s",
    }
  },

  wavebarsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    height: "30px",
    marginTop: "5px",
    marginBottom: "5px",
    marginLeft: "auto",
    marginRight: "auto",
    "--boxSize": "5px",
    "--gutter": "4px",
    width: "calc((var(--boxSize) + var(--gutter)) * 5)",
  },

  wavebars: {
    transform: "scaleY(.4)",
    height: "100%",
    width: "var(--boxSize)",
    animationDuration: "1.2s",
    backgroundColor: theme.palette.type === 'light' ? "#303030" : "#ffffff",
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    borderRadius: '8px',
  },

  wavebar1: {
    animationName: 'quiet'
  },
  wavebar2: {
    animationName: 'normal'
  },
  wavebar3: {
    animationName: 'quiet'
  },
  wavebar4: {
    animationName: 'loud'
  },
  wavebar5: {
    animationName: 'quiet'
  },
  linkPreviewThumbnail: {
    width: "328px",
    maxWidth: "100%",
    height: "172px",
  },
  linkPreviewTitle: {
    fontWeight: "bold",
    marginBottom: "4px"
  },
  linkPreviewDescription: {
    marginBottom: "4px"
  },
  linkPreviewUrl: {
    opacity: 0.6
  },
  linkPreviewAnchor: {
    textDecoration: "none",
    color: theme.palette.type === 'light' ? "#303030" : "#ffffff",
  },
  messageHighlighted: {
    backgroundColor: theme.palette.primary.main,
  },
  previewThumbnail: {
    width: "383px",
    maxWidth: "100%",
  },
  audioBottom: {
    marginBottom: "12px",
  },
  
  // WhatsApp-style audio player
  audioPlayerWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px 24px 10px", // Extra bottom padding for timestamp
    minWidth: 200,
    maxWidth: "100%",
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "transparent",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      gap: 6,
      padding: "8px 8px 24px 8px",
      minWidth: 180,
    },
  },
  
  audioPlayButton: {
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: "50%",
    backgroundColor: "#25D366",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "none",
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    "&:hover": {
      backgroundColor: "#128C7E",
    },
    "& svg": {
      color: "#fff",
      fontSize: 22,
    },
    [theme.breakpoints.down("sm")]: {
      width: 44,
      height: 44,
      "& svg": { fontSize: 22 },
    },
  },
  
  audioWaveform: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: 2,
    height: 32,
    overflow: "hidden",
    minWidth: 0,
    paddingLeft: 4,
    paddingRight: 4,
  },
  
  audioWaveBar: {
    width: 3,
    borderRadius: 3,
    backgroundColor: "rgba(0,0,0,0.25)",
    transition: "background-color 0.15s ease",
  },
  
  audioWaveBarPlayed: {
    backgroundColor: "#25D366",
  },
  
  audioTime: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.type === 'light' ? "#54656f" : "#8696a0",
    minWidth: 38,
    textAlign: "right",
  },
  
  audioAvatar: {
    width: 45,
    height: 45,
    borderRadius: "50%",
    marginLeft: -8,
    flexShrink: 0,
    border: `2px solid ${theme.palette.type === 'light' ? "#fff" : "#1f2c34"}`,
    [theme.breakpoints.down("sm")]: {
      width: 36,
      height: 36,
    },
    flexShrink: 0,
  },
  
  micIcon: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 16,
    height: 16,
    backgroundColor: "#00a884",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      fontSize: 10,
      color: "#fff",
    },
  },
  
  // Reaction emoji styles - WhatsApp-like bubble at bottom of message
  reactionEmoji: {
    position: "absolute",
    bottom: -10,
    right: 4,
    fontSize: 14,
    backgroundColor: theme.palette.type === 'light' ? "#ffffff" : "#1f2c34",
    borderRadius: 10,
    padding: "1px 4px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
    zIndex: 10,
    animation: "$reactionPop 0.15s ease-out",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 22,
    height: 20,
    border: `1px solid ${theme.palette.type === 'light' ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.1)"}`,
  },
  
  reactionEmojiLeft: {
    right: "auto",
    left: 4,
  },
  
  "@keyframes reactionPop": {
    "0%": {
      transform: "scale(0.5)",
      opacity: 0,
    },
    "70%": {
      transform: "scale(1.1)",
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {

      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "UPDATE_REACTION") {
    const { messageId, reaction } = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageId);

    if (messageIndex !== -1) {
      state[messageIndex] = { ...state[messageIndex], reaction };
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

// WhatsApp-style Audio Player Component
const WhatsAppAudioPlayer = ({ src, classes, fromMe, createdAt, isEdited, ack }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [waveformBars, setWaveformBars] = useState([]);
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);
  
  useEffect(() => {
    // Generate random waveform bars - fewer on mobile for better fit
    const isMobile = window.innerWidth <= 600;
    const barCount = isMobile ? 25 : 40;
    const bars = Array.from({ length: barCount }, () => Math.random() * 20 + 5);
    setWaveformBars(bars);
  }, []);
  
  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };
  
  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const handleAudioError = () => {
    setAudioError(true);
    setIsPlaying(false);
  };
  
  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const playedBars = Math.floor((progress / 100) * waveformBars.length);
  
  return (
    <div className={classes.audioPlayerWrapper}>
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={handleAudioError}
        style={{ display: 'none' }}
      >
        {/* Multiple source fallbacks so Safari/iOS can play ogg/opus voice notes.
            Browser tries each in order and picks the first format it supports. */}
        <source src={src} type="audio/ogg; codecs=opus" />
        <source src={src} type="audio/ogg" />
        <source src={src} type="audio/mp4" />
        <source src={src} type="audio/mpeg" />
        <source src={src} />
      </audio>
      {audioError && (
        <span style={{ fontSize: 11, color: "#999", padding: "0 8px" }}>
          לא ניתן להפעיל הקלטה זו
        </span>
      )}
      
      {/* Play/Pause Button */}
      <button className={classes.audioPlayButton} onClick={handlePlayPause}>
        {isPlaying ? <Pause /> : <PlayArrow />}
      </button>
      
      {/* Waveform */}
      <div className={classes.audioWaveform}>
        {waveformBars.map((height, index) => (
          <div
            key={index}
            className={`${classes.audioWaveBar} ${index < playedBars ? classes.audioWaveBarPlayed : ''}`}
            style={{ height: `${height}px` }}
          />
        ))}
      </div>
      
      {/* Time */}
      <span className={classes.audioTime}>
        {formatTime(isPlaying || currentTime > 0 ? currentTime : duration)}
      </span>
      
      {/* Avatar/Mic indicator for voice messages */}
      {!fromMe && (
        <div style={{ position: 'relative' }}>
          <Avatar className={classes.audioAvatar}>
            <Person />
          </Avatar>
          <div className={classes.micIcon}>
            <Mic />
          </div>
        </div>
      )}
      
      {/* Timestamp - positioned at bottom right */}
      {createdAt && (
        <span style={{ 
          position: 'absolute', 
          bottom: 4, 
          right: 8, 
          fontSize: 11, 
          color: '#667781',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          {isEdited && <span style={{ marginRight: 2 }}>{i18n.t("messagesList.edited")}</span>}
          {createdAt}
          {fromMe && ack !== undefined && (
            <span style={{ marginLeft: 3, display: 'flex', alignItems: 'center' }}>
              {ack === -1 && (
                <Tooltip title={i18n.t("messagesList.ack.failed")} arrow>
                  <ErrorOutline style={{ fontSize: 15, color: '#e53935' }} />
                </Tooltip>
              )}
              {ack === 0 && (
                <Tooltip title={i18n.t("messagesList.ack.pending")} arrow>
                  <AccessTime style={{ fontSize: 15, color: '#667781' }} />
                </Tooltip>
              )}
              {ack === 1 && (
                <Tooltip title={i18n.t("messagesList.ack.sent")} arrow>
                  <Done style={{ fontSize: 15, color: '#667781' }} />
                </Tooltip>
              )}
              {ack === 2 && (
                <Tooltip title={i18n.t("messagesList.ack.delivered")} arrow>
                  <DoneAll style={{ fontSize: 15, color: '#667781' }} />
                </Tooltip>
              )}
              {(ack === 3 || ack === 4) && (
                <Tooltip title={i18n.t("messagesList.ack.read")} arrow>
                  <DoneAll style={{ fontSize: 15, color: '#53bdeb' }} />
                </Tooltip>
              )}
            </span>
          )}
        </span>
      )}
    </div>
  );
};

// Perf: memoized per-message bubble. Appending a message re-renders only
// the new bubble instead of the whole list. Parent handlers/helpers come
// through one stable ctx (ref-backed Proxy) so memo isn't defeated by new
// closure identities; showSelectMessageCheckbox stays a real prop because it
// changes and toggles the checkbox render.
const MessageBubble = React.memo(function MessageBubble({ message, index, showSelectMessageCheckbox, ctx }) {
  const { classes, isGroup, parseDataJson, startBubbleLongPress, cancelBubbleLongPress, handleBubbleContextMenu, handleOpenQuickReactions, handleQuickReply, handleOpenMessageOptionsMenu, renderDailyTimestamps, renderMessageDivider, renderVCard, renderQuotedMessage, renderLinkPreview, messageLocation, checkMessageMedia, renderMessageAck, SelectMessageCheckbox, isVCard } = ctx;
      // Cached parse — see dataJsonCacheRef above. Unchanged messages hit the
      // cache; only new/edited messages pay the JSON.parse cost.
      const data = parseDataJson(message);
      // Check for sticker: Baileys uses dataJson.message.stickerMessage, Meta API uses mediaType or webp extension
      const isSticker = (data?.message && ("stickerMessage" in data.message)) || 
                        message.mediaType === "sticker" || 
                        message.mediaType === "image/webp" ||
                        (message.mediaUrl && message.mediaUrl.endsWith(".webp"));
      
      // Check if this is an audio/voice message - timestamp is rendered inside audio player
      const isAudioMessage = message.mediaType?.startsWith("audio") || 
                             message.mediaType === "ptt" ||
                             message.body === "[Voice message]" ||
                             message.body?.includes("Voice message") ||
                             data?.message?.audioMessage ||
                             data?.message?.pttMessage;

      const renderReactionEmoji = (isLeftMessage = false) => {
        if (message.reaction) {
          return (
            <span className={clsx(classes.reactionEmoji, {
              [classes.reactionEmojiLeft]: isLeftMessage,
            })}>
              {message.reaction}
            </span>
          );
        }
        return null;
      };
      if (!message.fromMe) {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderMessageDivider(message, index)}
            <div id={message.id}
              className={[clsx(classes.messageContainer, classes.messageLeft, {
                [classes.messageMediaSticker]: isSticker,
              })]}
              title={message.queueId && message.queue?.name}
              onTouchStart={(e) => startBubbleLongPress(e, message, data)}
              onTouchEnd={cancelBubbleLongPress}
              onTouchMove={cancelBubbleLongPress}
              onTouchCancel={cancelBubbleLongPress}
              onContextMenu={(e) => handleBubbleContextMenu(e, message)}
            >
           <SelectMessageCheckbox showSelectMessageCheckbox={showSelectMessageCheckbox} message={message} />
              {/* Sprint 8A — hover action row (😊 / ↩️ / ⌄). Replaces
                  the single ExpandMore button. id="messageActionsRow"
                  is matched by the bubble's &:hover selector to flip
                  display:none → flex. Disabled when the message is
                  deleted — there's nothing to act on. */}
              {!message.isDeleted && (
                <div className={classes.messageActionsRow}>
                  <IconButton
                    size="small"
                    className={classes.quickActionButton}
                    onClick={(e) => handleOpenQuickReactions(e, message, data)}
                    aria-label="react"
                  >
                    <EmojiEmotions />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={classes.quickActionButton}
                    onClick={(e) => handleQuickReply(e, message)}
                    aria-label="reply"
                  >
                    <Reply />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={classes.quickActionButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message, data)}
                    aria-label="more"
                  >
                    <ExpandMore />
                  </IconButton>
                </div>
              )}
              {isGroup && (
                <span className={classes.messageContactName}>
                  {message.contact?.name}
                </span>
              )}

              {message.thumbnailUrl && (
                <img className={classes.previewThumbnail} loading="lazy" decoding="async" src={message.thumbnailUrl} />
              )}

              {message.body.includes('data:image') ? messageLocation(message.body, message.createdAt)
                :
                isVCard(message.body) ?
                  <div
                    className={[clsx(classes.textContentItem, {
                      [classes.textContentItemEdited]: message.isEdited
                    }), { marginRight: 0 }]}>
                    {renderVCard(message.body)}
                  </div>

                  :

                  (<div className={[clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                    [classes.textContentItemEdited]: message.isEdited
                  }),]}>
                    {message.isForwarded && (
                      <span className={classes.forwardedLabel}>
                        <Reply className={classes.forwardedIcon} style={{ transform: 'scaleX(-1)' }} />
                        {i18n.t("message.forwarded")}
                        {message.forwardedFromContactName && ` ${i18n.t("message.from")} ${message.forwardedFromContactName}`}
                      </span>
                    )}
                    {message.quotedMsg && renderQuotedMessage(message)}
                    {renderLinkPreview(message)}
                    {!isSticker && (() => {
                      // Skip body entirely for media-only messages.
                      if (message.mediaUrl && !data?.message?.extendedTextMessage) return "";
                      // Skip auto-generated filenames passed through as body
                      // (audio-record-site-….mp3, IMG_….jpg) — they're not a
                      // real caption. Uses the shared AUTO_FILENAME_RE (real
                      // extensions only) so ordinary text that merely looks like
                      // "14-18.09" is NOT hidden. (Ticket #11436.)
                      const isAutoFilename = message.body && AUTO_FILENAME_RE.test(message.body);
                      if (isAutoFilename) return "";
                      return (
                        <>
                          {message.isDeleted && (
                            <Block
                              color="disabled"
                              fontSize="small"
                              className={classes.deletedIcon}
                            />
                          )}
                          {/* Sprint 8C — per-message direction. */}
                          <span dir={detectTextDirection(message.body)}>
                            <MarkdownWrapper>
                              {message.body}
                            </MarkdownWrapper>
                          </span>
                        </>
                      );
                    })()}
                    {!isAudioMessage && !(message.mediaUrl && !isSticker) && (
                      <span className={[clsx(classes.timestamp, {
                        [classes.timestampStickerLeft]: isSticker
                      })]}>
                        {message.isEdited && <span> {i18n.t("message.edited")} </span>}
                        {format(parseISO(message.createdAt), "HH:mm")}
                      </span>
                    )}
                  </div>)}
                  {message.mediaUrl && !data?.message?.extendedTextMessage && checkMessageMedia(message, data)}
                  {renderReactionEmoji(true)}
            </div>
          </React.Fragment>
        );
      } else {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderMessageDivider(message, index)}
            <div id={message.id}
              className={[clsx(classes.messageContainer, classes.messageRight, {
                [classes.messageMediaSticker]: isSticker,
              })]}
              title={message.queueId && message.queue?.name}
              onTouchStart={(e) => startBubbleLongPress(e, message, data)}
              onTouchEnd={cancelBubbleLongPress}
              onTouchMove={cancelBubbleLongPress}
              onTouchCancel={cancelBubbleLongPress}
              onContextMenu={(e) => handleBubbleContextMenu(e, message)}
            >
                            <SelectMessageCheckbox showSelectMessageCheckbox={showSelectMessageCheckbox} message={message} />

              {/* Sprint 8A — hover action row, outgoing side. */}
              {!message.isDeleted && (
                <div className={classes.messageActionsRow}>
                  <IconButton
                    size="small"
                    className={classes.quickActionButton}
                    onClick={(e) => handleOpenQuickReactions(e, message, data)}
                    aria-label="react"
                  >
                    <EmojiEmotions />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={classes.quickActionButton}
                    onClick={(e) => handleQuickReply(e, message)}
                    aria-label="reply"
                  >
                    <Reply />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={classes.quickActionButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message, data)}
                    aria-label="more"
                  >
                    <ExpandMore />
                  </IconButton>
                </div>
              )}
              <div
                className={clsx(classes.textContentItem, {
                  [classes.textContentItemDeleted]: message.isDeleted,
                  [classes.textContentItemEdited]: message.isEdited,
                })}
              >
                {message.isForwarded && (
                  <span className={classes.forwardedLabel}>
                    <Reply className={classes.forwardedIcon} style={{ transform: 'scaleX(-1)' }} />
                    {i18n.t("message.forwarded")}
                    {message.forwardedFromContactName && ` ${i18n.t("message.from")} ${message.forwardedFromContactName}`}
                  </span>
                )}
                {message.isDeleted && (
                  <Block
                    color="disabled"
                    fontSize="small"
                    className={classes.deletedIcon}
                  />
                )}
                {message.body.includes('data:image') ? messageLocation(message.body, message.createdAt)
                  :
                  isVCard(message.body) ?
                    <div className={[classes.textContentItem]}>
                      {renderVCard(message.body)}
                    </div>

                    :
                    message.quotedMsg && renderQuotedMessage(message)}
                {renderLinkPreview(message)}
                {!isSticker && (
                  // Show body text only for text-only messages (no media).
                  // For media+caption, checkMessageMedia renders the caption
                  // BELOW the video/image — don't duplicate it here above the media.
                  (() => {
                    const isTextOnly = !message.mediaUrl && message.body;
                    if (!isTextOnly) return null;
                    return (
                      <>
                        <span dir={detectTextDirection(message.body)}>
                          <MarkdownWrapper>{message.body}</MarkdownWrapper>
                        </span>
                      </>
                    );
                  })()
                )
                }
                {!isAudioMessage && !(message.mediaUrl && !isSticker) && (
                  <span className={[clsx(classes.timestamp, {
                    [classes.timestampStickerRight]: isSticker
                  })]}>
                    {message.isEdited && <span> {i18n.t("message.edited")} </span>}
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                )}

              </div>
              {message.mediaUrl && checkMessageMedia(message, data)}
              {renderReactionEmoji()}
            </div>
          </React.Fragment>
        );
      }
});

const MessagesList = ({ ticket, ticketId, isGroup, markAsRead, 
  showSelectMessageCheckbox,
  setShowSelectMessageCheckbox,
  setSelectedMessagesList,
  selectedMessagesList,
  forwardMessageModalOpen,
  setForwardMessageModalOpen }) => {
  const classes = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef();
  // Scroll anchor: saved before loadMore() prepends older messages so we can
  // restore the user's reading position after the list grows upward.
  // Raviv 2026-05-30: without this fix the scrollHeight increase pushed the
  // viewport to the very top of the newly-added messages instead of staying
  // at the same visual position the user was at.
  const savedScrollHeightRef = useRef(null);
  // Set true on ticket-open so the initial scroll-to-bottom runs from the
  // useLayoutEffect below (after the messages are in the DOM, before paint)
  // instead of synchronously with a stale scrollHeight — that stale-read was
  // the "chat jitters on open" jump.
  const pendingScrollBottomRef = useRef(false);
  // Set on socket "create" BEFORE the ADD_MESSAGE dispatch: true only when the
  // user was already reading at (near) the bottom, or the message is ours.
  // The useLayoutEffect on [messagesList] consumes it AFTER the new bubble is
  // in the DOM — the old synchronous scrollToBottom() ran against the
  // pre-bubble scrollHeight AND yanked the thread down while the user was
  // reading history.
  const stickToBottomRef = useRef(false);
  // Late-media re-pin (restored 01.07 overlay, adapted to the one-shot
  // stickToBottomRef above): armed when a message lands while the user is at
  // the bottom (or it's ours), disarmed when the operator scrolls up. While
  // armed, a late-loading image/video that grows its bubble re-pins the view
  // to the bottom — so you land exactly on the last message, never a few
  // messages above it. Unlike stickToBottomRef it survives the layout-effect
  // consume, because media finishes loading well after that effect ran.
  const mediaStickArmedRef = useRef(false);
  // Per-message dataJson parse cache. renderMessages used to JSON.parse every
  // message's dataJson on EVERY render (and renderLinkPreview parsed the same
  // string a second time), so each ack ripple (sent→delivered→read) re-parsed
  // the whole thread — visible scroll stutter on 360px phones. Keyed by
  // message.id and invalidated when the raw string changes (edits). Cleared on
  // ticket switch so it can't grow unbounded.
  const dataJsonCacheRef = useRef(new Map());
  const parseDataJson = (message) => {
    const cached = dataJsonCacheRef.current.get(message.id);
    if (cached && cached.raw === message.dataJson) return cached.data;
    let parsed = null;
    try {
      parsed = message.dataJson ? JSON.parse(message.dataJson) : null;
    } catch (e) {
      parsed = null;
    }
    dataJsonCacheRef.current.set(message.id, { raw: message.dataJson, data: parsed });
    return parsed;
  };
  const isRTL = localStorage.getItem('i18nextLng');


  const [selectedMessage, setSelectedMessage] = useState({});
  const [selectedMessageData, setSelectedMessageData] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  const [contactPresence, setContactPresence] = useState("available");
  // Route uuid for the instant-switch containment guard below. Mirrored into
  // a ref because the appMessage socket effect keys on [ticketId, ticket,
  // socketManager] and deliberately does NOT re-subscribe on a route change —
  // a plain closure over the param would go stale exactly during the lag
  // window the guard exists for (route already on the new uuid, previous
  // ticket still mounted). In TicketMessagesDialog this is harmlessly empty
  // (and markAsRead is undefined there anyway).
  const { ticketId: routeUuidParam } = useParams();
  const routeUuidRef = useRef(routeUuidParam);
  routeUuidRef.current = routeUuidParam;

  // Sprint 8A — quick-reactions popover anchor: when the user clicks the
  // 😊 button in the hover-row, we open the existing reactions sub-menu
  // directly (skipping the full options menu). Sprint 8D — show/hide
  // scroll-to-bottom FAB based on scroll distance from the bottom.
  const [quickReactionAnchor, setQuickReactionAnchor] = useState(null);
  const [showScrollDown, setShowScrollDown] = useState(false);

  // Voice calls for this contact, rendered inline in the timeline (additive —
  // a separate state, never mixed into the message reducer/hot path).
  const [callEvents, setCallEvents] = useState([]);
  const contactId = ticket?.contactId || ticket?.contact?.id;

  const socketManager = useContext(SocketContext);
  const { setReplyingMessage } = useContext(ReplyMessageContext);

  function loadData(incrementPage = false) {
    setLoading(true);
    const thisPageNumber = incrementPage ? pageNumber + 1 : 1;

    // Scroll anchor: capture current scrollHeight BEFORE the new (older)
    // messages are prepended. useLayoutEffect below will use this to adjust
    // scrollTop so the user stays at the same visual position.
    if (incrementPage && scrollRef.current) {
      savedScrollHeightRef.current = scrollRef.current.scrollHeight;
    }

    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === undefined) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber: thisPageNumber, markAsRead },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          // Initial scroll-to-bottom is handled by the useLayoutEffect on
          // [messagesList] (pendingScrollBottomRef) so it fires after render,
          // before paint — no stale-scrollHeight jump. (Removed the synchronous
          // scrollToBottom() that ran here before the messages were in the DOM.)
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
      setPageNumber(thisPageNumber);
      // Initial open fires at most once per ticketId (the effect below), so the
      // 500ms debounce only added half a second of blank chat on every switch.
      // Keep it for scroll-driven pagination (loadMore) where rapid re-entry is
      // real; the ticket-open path fetches on the next tick.
    }, incrementPage ? 500 : 0);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }

  useEffect(async () => {
    dispatch({ type: "RESET" });
    dataJsonCacheRef.current.clear();
    setContactPresence("available");

    currentTicketId.current = ticketId;
    // Opening this ticket: the first page should land at the bottom. Flag it
    // so the useLayoutEffect scrolls once the messages have rendered. Also arm
    // the late-media re-pin: images/videos near the bottom finish loading
    // after that layout effect ran, and their height growth would otherwise
    // strand the view a few messages above the last one. (Assigning here also
    // overwrites any stale armed/disarmed carry-over from the previous ticket.)
    pendingScrollBottomRef.current = true;
    mediaStickArmedRef.current = true;

    await loadPageMutex.runExclusive(async () => {
      loadData();
    });
  }, [ticketId]);

  // Backfill the open conversation when the native app returns to the
  // foreground (useCapacitorResume dispatches "app-resumed"). This list is
  // reducer-based, not react-query, so the tickets-query invalidate on resume
  // doesn't touch it, and the socket onConnect is a one-shot that won't
  // re-fire on forceReconnect — so we reload the still-open ticket here.
  // Web never dispatches "app-resumed" (the hook is isNativeApp-gated),
  // so this listener is inert in the browser.
  // NOTE: this must NOT be a bare loadData(): LOAD_MESSAGES prepends unseen
  // messages (correct for paginating OLDER pages), so feeding it page 1 after
  // a suspension would insert the missed newest messages at the TOP of the
  // transcript, out of order. Instead: fetch page 1 first, and only on
  // success RESET + reload (atomic swap) — chronological order guaranteed,
  // pendingScrollBottomRef lands the user on the newest message, and a failed
  // fetch (network still waking up) leaves the existing transcript intact.
  useEffect(() => {
    const onAppResumed = async () => {
      if (currentTicketId.current !== ticketId || ticketId === undefined) return;
      try {
        // Fetch FIRST, swap only on success: resume is exactly when the
        // WebView's network may still be re-establishing (radio wake, captive
        // portal) — a RESET before a failed fetch would destroy the transcript
        // the user already has and leave the open conversation blank with no
        // retry. On failure we keep the existing list; the reconnected socket
        // (or the next resume) catches up.
        const { data } = await api.get("/messages/" + ticketId, {
          params: { pageNumber: 1, markAsRead },
        });
        if (currentTicketId.current !== ticketId) return; // switched mid-fetch
        dispatch({ type: "RESET" });
        dataJsonCacheRef.current.clear();
        setContactPresence("available");
        pendingScrollBottomRef.current = true;
        // Same as the ticket-open path above: the resume backfill lands at the
        // bottom, so arm the late-media re-pin for media that loads after the
        // pin-to-bottom layout effect consumed the flag.
        mediaStickArmedRef.current = true;
        dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
        setHasMore(data.hasMore);
        setPageNumber(1);
        setLoading(false);
      } catch (err) {
        /* keep the current transcript — never blank the chat on resume */
      }
    };
    window.addEventListener("app-resumed", onAppResumed);
    return () => {
      window.removeEventListener("app-resumed", onAppResumed);
    };
    // loadData is intentionally omitted (recreated every render); re-subscribe
    // only when the open ticket changes — same contract as the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // Scroll anchor: fires synchronously after the DOM has updated (before
  // the browser paints) so the position correction is invisible to the user.
  // When older messages are prepended (loadMore), scrollHeight grows upward.
  // Without this correction the browser keeps scrollTop fixed, which makes
  // the viewport jump to wherever the new content starts. We undo that jump
  // by adding the height delta back to scrollTop.
  useLayoutEffect(() => {
    if (savedScrollHeightRef.current !== null && scrollRef.current) {
      const delta = scrollRef.current.scrollHeight - savedScrollHeightRef.current;
      if (delta > 0) {
        scrollRef.current.scrollTop += delta;
      }
      savedScrollHeightRef.current = null;
      return;
    }
    // Initial open: land at the bottom AFTER the DOM mutation but BEFORE the
    // browser paints, so there is no visible jump/jitter (the canonical chat
    // scroll-to-bottom pattern — useLayoutEffect, not a synchronous call on a
    // pre-render stale scrollHeight).
    if (pendingScrollBottomRef.current && messagesList.length > 0 && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      pendingScrollBottomRef.current = false;
      stickToBottomRef.current = false;
      return;
    }
    // Inbound/outbound "create": stick to the bottom only when the flag was
    // captured true before the dispatch (user was already at the bottom, or
    // it's our own message). Runs after the new bubble is committed, so the
    // scrollHeight read here is the post-bubble one.
    if (stickToBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      stickToBottomRef.current = false;
    }
  }, [messagesList]);

  useEffect(() => {
    if (!ticket.id) {
      return;
    }

    const companyId = localStorage.getItem("companyId");

    const socket = socketManager.GetSocket(companyId);

    const onConnect = () => {
      socket.emit("joinChatBox", `${ticket.id}`);
    }

    // 2026-06-17 fix: join the ticket room IMMEDIATELY, not only via the
    // onConnect/onReady callback. The callback path has a missed-event window —
    // if the socket "ready" event already fired before this component mounted
    // (the normal case when switching tickets in an already-connected SPA), the
    // onReady handler is registered for an event that never fires again, so
    // joinChatBox was never emitted → the open chat never joined the ticket room
    // → outbound/inbound messages did not appear live (only after a refresh, on
    // WhatsApp AND omnichannel). Mirrors the already-in-production pattern in
    // Ticket/index.js:153 and TicketMessagesDialog/index.js:147. joinChatBox is
    // idempotent server-side and deduped in the manager's joins Set.
    socket.emit("joinChatBox", `${ticket.id}`);

    socketManager.onConnect(onConnect);

    const onAppMessage = async (data) => {
      // Debug log for ack updates
      if (data.action === "update") {
        console.log("[MessagesList] Received message update:", {
          messageTicketId: data.message.ticketId,
          currentTicketId: currentTicketId.current,
          messageAck: data.message.ack,
          willUpdate: data.message.ticketId == currentTicketId.current
        });
      }
      // Use loose comparison to handle number vs string type mismatch
      // eslint-disable-next-line eqeqeq
      if (data.message.ticketId == currentTicketId.current) {
        setContactPresence("available");
        if (data.action === "create") {
          // Capture the "was the user at the bottom?" answer BEFORE the
          // dispatch — after it, scrollHeight already includes the new bubble
          // and the read is meaningless. Same near-bottom window (¼ viewport)
          // as onPresence below. Own outgoing messages always stick.
          if (scrollRef.current) {
            const { scrollTop, clientHeight, scrollHeight } = scrollRef.current;
            stickToBottomRef.current =
              data.message.fromMe ||
              scrollTop + clientHeight >= scrollHeight - clientHeight / 4;
          } else {
            stickToBottomRef.current = true;
          }
          // Arm the late-media re-pin whenever we decided to stick: images/
          // videos in this (or an adjacent) bubble finish loading after the
          // layout effect consumed stickToBottomRef, and their height growth
          // would otherwise leave the view a few messages above the bottom.
          if (stickToBottomRef.current) {
            mediaStickArmedRef.current = true;
          }
          dispatch({ type: "ADD_MESSAGE", payload: data.message });

          // Mark as read immediately if user is viewing this ticket
          // and it's an incoming message (not from the agent).
          // Instant-switch containment: while the previous ticket is still
          // mounted (kept visible) after the route already moved to a new
          // uuid, the agent is NOT actually viewing this ticket — auto-
          // marking it read here would silently clear the unread badge of a
          // chat nobody looked at. Skip ONLY the markAsRead call; the
          // ADD_MESSAGE dispatch above still lands the bubble.
          if (!data.message.fromMe && markAsRead) {
            const routeUuidNow = routeUuidRef.current;
            if (!(routeUuidNow && ticket?.uuid && ticket.uuid !== routeUuidNow)) {
              try {
                await api.get(`/messages/${currentTicketId.current}`, {
                  params: { pageNumber: 1, markAsRead: true }
                });
              } catch (err) {
                console.error("Failed to mark message as read:", err);
              }
            }
          }
        }

        if (data.action === "update") {
          dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
        }
      }
    }

    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    const onPresence = (data) => {
      if (!scrollRef.current) return;
      const { scrollTop, clientHeight, scrollHeight } = scrollRef.current;
      const isAtBottom = scrollTop + clientHeight >= (scrollHeight - clientHeight / 4);
      if (data?.ticketId === ticket.id) {
        setContactPresence(data.presence);
        if (["composing", "recording"].includes(data.presence)) {
          if (isAtBottom) {
            scrollToBottom();
          }
        }
      }
    };
    
    socket.on(`company-${companyId}-presence`, onPresence);

    return () => {
      // Remove specific listeners instead of disconnecting entire socket
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
      socket.off(`company-${companyId}-presence`, onPresence);
    };
  }, [ticketId, ticket, socketManager]);

  // Voice — load this contact's calls and live-update them inline in the
  // timeline. Fully isolated from the message reducer: its own state, its own
  // socket subscription (`company-<id>-call`). Fails silent when Voice is off.
  useEffect(() => {
    let mounted = true;
    if (!contactId) {
      setCallEvents([]);
      return undefined;
    }
    const refetch = () =>
      apiVoice
        .listCallsByContact(contactId)
        .then((calls) => mounted && setCallEvents(Array.isArray(calls) ? calls : []))
        .catch(() => {});
    refetch();

    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.GetSocket(companyId);
    let refetchTimer = null;
    const onCall = (payload) => {
      const call = payload && payload.call;
      // eslint-disable-next-line eqeqeq
      if (!call || call.contactId != contactId) return;
      setCallEvents((prev) => {
        const idx = prev.findIndex((c) => c.id === call.id);
        if (idx === -1) return [...prev, call];
        const next = prev.slice();
        next[idx] = { ...next[idx], ...call };
        return next;
      });
      // Recording/transcript/AI are produced asynchronously after a call ends —
      // refetch once a few seconds later to pull the enriched artifacts in.
      if (payload.action === "ended" || payload.action === "outcome") {
        if (refetchTimer) clearTimeout(refetchTimer);
        refetchTimer = setTimeout(refetch, 12000);
      }
    };
    socket.on(`company-${companyId}-call`, onCall);
    return () => {
      mounted = false;
      if (refetchTimer) clearTimeout(refetchTimer);
      socket.off(`company-${companyId}-call`, onCall);
    };
  }, [contactId, socketManager]);

  const loadMore = async () => {
    await loadPageMutex.runExclusive(async () => {
      loadData(true);
    });
  }
    const handleSelectMessage = (e, message) => {
      const list = selectedMessagesList;
      if (e.target.checked) {
        list.push(message);
      } else {
        if (list.length >= 4) {
          toastError({ response: { data: { message: "4 messages max." } } });
          return;
        }
        const index = list.findIndex((m) => m.id === message.id);
        list.splice(index, 1);
      }
      setSelectedMessagesList(list);
    }
  
    const SelectMessageCheckbox = ({ message, showSelectMessageCheckbox }) => {
      if (showSelectMessageCheckbox) {
        return <Checkbox aria-label="" color="primary" onChange={(e) => handleSelectMessage(e, message)} />;
      } else {
        return null;
      }
    };


  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = el;

    // Sprint 8D — toggle scroll-to-bottom FAB. Threshold of 200px
    // matches WhatsApp Web behaviour: the button appears as soon as
    // you scroll up far enough that the latest bubble is off-screen
    // but doesn't flicker while you're at the very bottom.
    const scrolledUp = scrollTop + clientHeight < scrollHeight - 200;
    setShowScrollDown(scrolledUp);
    // Once the operator scrolls up off the bottom, stop the late-media
    // re-pin so we never yank them back down while they read history.
    if (scrolledUp) mediaStickArmedRef.current = false;

    if (!hasMore) return;

    // (Removed the legacy scrollTop=1 nudge that fired whenever the user hit
    // the very top — it fought iOS momentum scrolling with a synchronous
    // write per frame. savedScrollHeightRef already anchors the viewport
    // after loadMore prepends, so the nudge had nothing left to protect.)

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message, data) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
    setSelectedMessageData(data);
  };

  const handleCloseMessageOptionsMenu = (e) => {
        setSelectedMessagesList([])

    setAnchorEl(null);
  };

  // Touch path for the :hover-only action row (Android audit): the
  // react/reply/more row only appears on :hover, so on phones the message
  // actions were unreachable. A ~480ms long-press on a bubble opens the same
  // options menu, anchored to the pressed bubble (WhatsApp parity). Cancelled
  // if the finger lifts or moves (scroll) before the timer fires.
  const longPressTimer = useRef(null);
  // True while a finger is down on a bubble. Used to tell a touch-generated
  // contextmenu (Android long-press — already owned by the options-menu
  // long-press above, whose menu includes Copy) apart from a desktop
  // right-click, which is the copy shortcut below.
  const bubbleTouchActiveRef = useRef(false);
  // True once the 480ms timer fired (menu already opened) while the finger is
  // still down. The finger lift then makes the browser synthesize a click —
  // which would land on the MUI menu's backdrop and instantly close the menu
  // that just opened. touchend consumes this flag to preventDefault that
  // synthesized click.
  const longPressFiredRef = useRef(false);
  const startBubbleLongPress = (e, message, data) => {
    const anchor = e.currentTarget;
    bubbleTouchActiveRef.current = true;
    longPressFiredRef.current = false;
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      longPressFiredRef.current = true;
      handleOpenMessageOptionsMenu({ currentTarget: anchor }, message, data);
    }, 480);
  };
  const cancelBubbleLongPress = (e) => {
    bubbleTouchActiveRef.current = false;
    clearTimeout(longPressTimer.current);
    if (!longPressFiredRef.current) return;
    // Long-press already opened the menu. Suppress the click the browser
    // synthesizes on finger-lift (see longPressFiredRef above) and disarm.
    // touchmove keeps the flag armed — the finger is still down and the
    // synthesized click (if any) comes from the upcoming touchend.
    if (e && e.type === "touchend") {
      if (e.cancelable) e.preventDefault();
      longPressFiredRef.current = false;
    } else if (e && e.type === "touchcancel") {
      longPressFiredRef.current = false;
    }
  };

  // Late-media re-pin (restored 01.07 overlay): when a late-loading image or
  // video finishes and grows its bubble, re-pin to the bottom — but only while
  // armed (see mediaStickArmedRef), so we never yank a user reading history.
  const restickOnMedia = () => {
    if (mediaStickArmedRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  // Desktop right-click on a bubble copies its text — like WhatsApp Web
  // (restored 01.07 overlay). On touch the long-press gesture already opens
  // the options menu (which has Copy), so touch-generated contextmenu events
  // are left to their existing behaviour via the bubbleTouchActiveRef guard.
  const copyMessageText = (message) => {
    const text = message?.body || "";
    if (!text) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("הועתק");
    } catch (e) { /* clipboard blocked — silently ignore */ }
  };
  const handleBubbleContextMenu = (e, message) => {
    // Touch long-press owns this gesture (options menu, incl. Copy) — keep
    // the native behaviour untouched for touch-generated contextmenu events.
    if (bubbleTouchActiveRef.current) return;
    // Don't hijack right-click while the forward-select checkboxes are
    // active — there the interaction belongs to selection, not copy.
    if (showSelectMessageCheckbox) return;
    e.preventDefault();
    copyMessageText(message);
  };

  // Sprint 8A — hover-row handlers. Reply is a direct call to the
  // ReplyingMessage context (no menu round-trip); Quick reactions
  // opens the existing reactions sub-menu inside MessageOptionsMenu
  // via the quickReactionAnchor prop bridge. Both stopPropagation so
  // the click doesn't bubble up to the bubble itself (which has
  // its own select-checkbox handler in some sub-trees).
  const handleQuickReply = (e, message) => {
    e.stopPropagation();
    setReplyingMessage(message);
  };

  const handleOpenQuickReactions = (e, message, data) => {
    e.stopPropagation();
    setSelectedMessage(message);
    setSelectedMessageData(data);
    setQuickReactionAnchor(e.currentTarget);
  };

  const handleCloseQuickReactions = () => {
    setQuickReactionAnchor(null);
  };

  const checkMessageMedia = (message, data) => {
    const document =
      data?.message?.documentMessage
      || data?.message?.documentWithCaptionMessage?.message?.documentMessage;
    
    // Determine media type from message.mediaType, document mimetype, or from URL extension
    const mediaUrl = message.mediaUrl || "";
    const mediaType = message.mediaType?.toLowerCase() || "";
    const urlLower = mediaUrl.toLowerCase();
    const documentMimeType = document?.mimetype?.toLowerCase() || "";
    
    // Check if it's an image by mediaType, document mimetype, or file extension
    const isImage = mediaType === "image" || 
                    mediaType.startsWith("image/") ||
                    documentMimeType.startsWith("image/") ||
                    urlLower.match(/\.(jpg|jpeg|png|gif|bmp|tiff|heic|heif|svg|ico|raw|cr2|nef|arw|dng)(\?|$)/i);
    
    // Check if it's a video by mediaType, document mimetype, or file extension
    // Videos can come as documentMessage with video/* mimetype
    const isVideo = mediaType === "video" || 
                    mediaType.startsWith("video/") ||
                    documentMimeType.startsWith("video/") ||
                    urlLower.match(/\.(mp4|webm|ogg|mov|avi|mkv|3gp|m4v|wmv|flv|mpeg|mpg|3g2|ts|mts|m2ts|vob|asf|f4v|hevc)(\?|$)/i);
    
    // Check if it's audio by mediaType, document mimetype, file extension, or structured data.
    // isVoiceMessage detection is intentionally strict about body-text matching:
    //   - exact "[Voice message]" match is safe (WhatsApp canonical marker)
    //   - body.includes("Voice message") is INTENTIONALLY excluded — a video
    //     captioned "Voice message from..." would be misidentified as audio and
    //     rendered as an audio player (black screen instead of video).
    //     We rely on pttMessage / audioMessage fields from dataJson instead.
    // isVideo is checked first in the render tree, so a pure audio check that
    // fires on a video file is effectively guarded — but we keep the detection
    // tight to avoid the false-positive in the first place.
    const isVoiceMessage = message.body === "[Voice message]" ||
                           data?.message?.audioMessage ||
                           data?.message?.pttMessage;
    const isAudio = mediaType === "audio" ||
                    mediaType.startsWith("audio/") ||
                    mediaType === "ptt" ||
                    documentMimeType.startsWith("audio/") ||
                    urlLower.match(/\.(mp3|ogg|wav|m4a|aac|opus|flac|wma|amr|3ga|oga|spx|caf)(\?|$)/i) ||
                    isVoiceMessage;
    
    // Handle stickers (webp images) - check mediaType or webp extension
    const isSticker = mediaType === "sticker" || 
                      mediaType === "image/webp" ||
                      urlLower.endsWith(".webp");
    
    // 1. Stickers
    if (isSticker && !isVideo) {
      return (
        <img 
          src={mediaUrl} 
          alt="Sticker"
          style={{ 
            maxWidth: 200, 
            maxHeight: 200,
            backgroundColor: "transparent"
          }}
        />
      );
    }
    
    // 2. Audio - check BEFORE video since voice messages may have mp4 extension
    if (isAudio) {
      return (
        <WhatsAppAudioPlayer 
          src={mediaUrl} 
          classes={classes}
          fromMe={message.fromMe}
          createdAt={format(parseISO(message.createdAt), "HH:mm")}
          isEdited={message.isEdited}
          ack={message.ack}
        />
      );
    }
    
    // 3. Videos
    if (isVideo) {
      // Get thumbnail for video poster
      const videoThumbnail = message.thumbnailUrl ||
                             data?.message?.videoMessage?.jpegThumbnail;
      const posterUrl = videoThumbnail
        ? (videoThumbnail.startsWith('data:') || videoThumbnail.startsWith('http')
          ? videoThumbnail
          : `data:image/jpeg;base64,${videoThumbnail}`)
        : undefined;
      // Caption: prefer structured fields; fall back to message.body for
      // outgoing messages where Baileys may not populate videoMessage.caption.
      // Filter out auto-placeholders and auto-filenames.
      const bodyIsPlaceholder = /^\[(Image|Video|Document|Voice message|Audio|Sticker).*\]$/i.test(message.body);
      const bodyIsAutoFile = message.body && AUTO_FILENAME_RE.test(message.body);
      const bodyCaption = (!bodyIsPlaceholder && !bodyIsAutoFile) ? message.body : null;
      const videoCaption = document?.caption || data?.message?.videoMessage?.caption || bodyCaption;
      // WhatsApp pattern (Raviv 2026-06-08): when the media has no caption,
      // render the timestamp + ack as a small chip overlaid on the bottom-right
      // of the media itself. Without this, the timestamp lives in an empty
      // text div under the media and floats in space.
      return (
        <>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <video
              className={classes.messageVideo}
              src={mediaUrl}
              poster={posterUrl}
              controls
              playsInline
              preload="metadata"
              onLoadedMetadata={restickOnMedia}
            />
          </div>
          {videoCaption && (
            <div className={clsx({
              [classes.textContentItemDeleted]: message.isDeleted,
              [classes.textContentItem]: !message.isDeleted,
            })}>
              <MarkdownWrapper>
                {videoCaption}
              </MarkdownWrapper>
            </div>
          )}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            gap: 3, padding: '3px 8px 4px 8px',
            fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', letterSpacing: 0.2,
            color: 'rgba(15,23,42,0.55)',
          }}>
            {format(parseISO(message.createdAt), "HH:mm")}
            {message.fromMe && renderMessageAck(message)}
          </div>
        </>
      );
    }

    // 4. Images (not stickers)
    if (isImage && !isSticker) {
      const imgBodyIsPlaceholder = /^\[(Image|Video|Document|Voice message|Audio|Sticker).*\]$/i.test(message.body);
      const imgBodyIsAutoFile = message.body && AUTO_FILENAME_RE.test(message.body);
      const imgBodyCaption = (!imgBodyIsPlaceholder && !imgBodyIsAutoFile) ? message.body : null;
      const imageCaption = data?.message?.imageMessage?.caption || document?.caption || imgBodyCaption;
      return (
        <>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <ModalImageCors imageUrl={mediaUrl} isDeleted={message.isDeleted} onImgLoad={restickOnMedia} />
          </div>
          {imageCaption && (
            <div className={clsx({
              [classes.textContentItemDeleted]: message.isDeleted,
              [classes.textContentItem]: !message.isDeleted,
            })}>
              <MarkdownWrapper>
                {imageCaption}
              </MarkdownWrapper>
            </div>
          )}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            gap: 3, padding: '3px 8px 4px 8px',
            fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', letterSpacing: 0.2,
            color: 'rgba(15,23,42,0.55)',
          }}>
            {format(parseISO(message.createdAt), "HH:mm")}
            {message.fromMe && renderMessageAck(message)}
          </div>
        </>
      );
    }
    
    // 5. Document/File download (fallback for actual documents)
    if (document || mediaUrl) {
      // 2026-06-11: omnichannel docs have no dataJson, so document is undefined
      // and message.body is a "[file]" placeholder → the card showed "[file]"
      // named "FILE". Fall back to the stored URL basename (which carries the
      // real extension, e.g. <uuid>.pdf) when the body is a placeholder.
      const isPlaceholderBody = !!message.body && /^\[(image|video|audio|file|document|sticker)\b/i.test(message.body);
      const urlBase = mediaUrl ? decodeURIComponent((mediaUrl.split("?")[0].split("/").pop()) || "") : "";
      const rawName = document?.fileName
        || (isPlaceholderBody ? (urlBase || "Download") : (message.body || urlBase || "Download"));
      // Drop any extension from the displayed name for a cleaner
      // chip — keep it for the small label below the name. The
      // entire card is the download link; no inner button so the
      // overlapping start/end icons from the old <Button> are gone.
      const extMatch = rawName.match(/\.([a-z0-9]{1,8})$/i);
      const ext = extMatch ? extMatch[1].toUpperCase() : "FILE";
      const displayName = extMatch ? rawName.slice(0, -extMatch[0].length) : rawName;
      const sizeBytes = document?.fileLength || document?.fileSize;
      const fmtSize = (n) => {
        if (!n || isNaN(n)) return null;
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
        return `${(n / (1024 * 1024)).toFixed(1)} MB`;
      };
      const sizeLabel = fmtSize(Number(sizeBytes));
      // Captions: only render the body if it's actually a caption
      // (different from the file name AND not an auto-generated
      // filename-shaped string). Filenames passed through MarkdownWrapper
      // produced the underscores-as-italics artefact in the bubble
      // below the card.
      const hasRealCaption =
        message.body &&
        !isPlaceholderBody &&
        message.body !== document?.fileName &&
        !AUTO_FILENAME_RE.test(message.body);
      return (
        <>
          <a
            href={mediaUrl.replace(/%/g, "%25")}
            target="_blank"
            rel="noopener noreferrer"
            download={rawName}
            className={classes.documentCard}
          >
            <span className={classes.documentCardIcon}>
              <Description />
            </span>
            <span className={classes.documentCardBody}>
              <span className={classes.documentCardName} title={rawName}>
                {displayName || rawName}
              </span>
              <span className={classes.documentCardMeta}>
                {ext}
                {sizeLabel ? ` · ${sizeLabel}` : ""}
              </span>
            </span>
            <span className={classes.documentCardIcon} aria-hidden="true">
              <GetApp />
            </span>
          </a>
          {hasRealCaption && (
            <div className={clsx({
              [classes.textContentItemDeleted]: message.isDeleted,
            })}>
              <MarkdownWrapper>
                {message.body}
              </MarkdownWrapper>
            </div>
          )}
        </>
      );
    }
    
    return null;
  };

  const renderMessageAck = (message) => {
    if (message.ack === -1) {
      return (
        <Tooltip title={i18n.t("messagesList.ack.failed")} arrow>
          <ErrorOutline fontSize="small" style={{ color: "#e53935", fontSize: 15 }} />
        </Tooltip>
      );
    }
    if (message.ack === 0) {
      return (
        <Tooltip title={i18n.t("messagesList.ack.pending")} arrow>
          <AccessTime fontSize="small" className={classes.ackIcons} />
        </Tooltip>
      );
    }
    if (message.ack === 1) {
      return (
        <Tooltip title={i18n.t("messagesList.ack.sent")} arrow>
          <Done fontSize="small" className={classes.ackIcons} />
        </Tooltip>
      );
    }
    if (message.ack === 2) {
      return (
        <Tooltip title={i18n.t("messagesList.ack.delivered")} arrow>
          <DoneAll fontSize="small" className={classes.ackIcons} />
        </Tooltip>
      );
    }
    if (message.ack === 3 || message.ack === 4) {
      return (
        <Tooltip title={i18n.t("messagesList.ack.read")} arrow>
          <DoneAll fontSize="small" className={classes.ackDoneReadIcon} />
        </Tooltip>
      );
    }
    return null;
  };

  const formatDateLabel = (date) => {
    if (isToday(date)) return i18n.t("messagesList.header.today") || "Today";
    if (isYesterday(date)) return i18n.t("messagesList.header.yesterday") || "Yesterday";
    return format(date, "dd/MM/yyyy");
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {formatDateLabel(parseISO(messagesList[index].createdAt))}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {formatDateLabel(parseISO(messagesList[index].createdAt))}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const scrollToMessage = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });

      // Add the highlight class
      element.classList.add(classes.messageHighlighted);

      // Remove the highlight class after 2 seconds
      setTimeout(() => {
        element.classList.remove(classes.messageHighlighted);
      }, 2000);
    }
  };

  // 2026-06-08 (Gal): classify the quoted media so both the preview
  // text and the thumbnail can render WhatsApp-style. The previous
  // implementation only handled imageMessage from Baileys dataJson —
  // a reply to a video/document showed the raw placeholder body
  // ("[Video]", "VIDEO") next to no thumbnail.
  const classifyQuotedMedia = (quotedMsg, data) => {
    if (!quotedMsg) return { kind: "text" };

    // Body placeholders that the backend emits when the body has no
    // caption. Channel-agnostic — both Cloud API and Baileys produce
    // these.
    const body = quotedMsg.body || "";
    const isImagePlaceholder = /^\[?Image\]?$/i.test(body) || body === "IMAGE";
    const isVideoPlaceholder = /^\[?Video\]?$/i.test(body) || body === "VIDEO";
    const isAudioPlaceholder = /^\[?(Voice message|Audio|PTT)\]?$/i.test(body);
    const isDocPlaceholder = /^\[?Document\]?$/i.test(body) || body === "DOCUMENT";
    const isStickerPlaceholder = /^\[?Sticker\]?$/i.test(body);

    // mediaType is the source of truth for Cloud API messages — it
    // mirrors WhatsApp Cloud's `type` field. Baileys variants
    // ("imageMessage", "videoMessage", …) covered too.
    const mt = (quotedMsg.mediaType || "").toLowerCase();
    const isImageType = isImagePlaceholder || mt === "image" || mt === "imagemessage";
    const isVideoType = isVideoPlaceholder || mt === "video" || mt === "videomessage";
    const isAudioType =
      isAudioPlaceholder ||
      mt === "audio" || mt === "ptt" || mt === "audiomessage" || mt === "pttmessage" ||
      data?.message?.audioMessage || data?.message?.pttMessage;
    const isDocType = isDocPlaceholder || mt === "document" || mt === "documentmessage";
    const isStickerType = isStickerPlaceholder || mt === "sticker" || mt === "stickermessage";

    // jpegThumbnail comes from Baileys dataJson. Cloud API doesn't
    // populate it, so we also fall back to mediaUrl regex for an
    // <img> preview when the URL ends in an image extension.
    const jpegThumb =
      data?.message?.imageMessage?.jpegThumbnail ||
      data?.message?.videoMessage?.jpegThumbnail ||
      data?.message?.documentMessage?.jpegThumbnail;
    const mediaUrl = quotedMsg.mediaUrl;
    const urlIsImage = mediaUrl && /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(mediaUrl);

    const imagePreviewUrl = jpegThumb
      ? `data:image/jpeg;base64,${jpegThumb}`
      : urlIsImage
      ? mediaUrl
      : null;

    if (isAudioType) return { kind: "audio" };
    if (isStickerType) return { kind: "sticker", imagePreviewUrl };
    if (isVideoType) return { kind: "video", imagePreviewUrl };
    if (isImageType) return { kind: "image", imagePreviewUrl };
    if (isDocType) return { kind: "document" };
    return { kind: "text" };
  };

  const getQuotedMessageText = (quotedMsg, kind) => {
    // Don't echo the media URL/filename when the body equals the file
    // (legacy Baileys behaviour for media without caption).
    if (quotedMsg.mediaUrl?.endsWith(quotedMsg.body)) {
      return "";
    }

    if (isVCard(quotedMsg.body)) {
      return "🪪";
    }

    // Friendly labels per kind. WhatsApp-style: icon + short label so
    // a reply to a video reads "🎬 סרטון" not "VIDEO". When a real
    // caption exists, fall through to it instead of the placeholder.
    const hasRealCaption =
      quotedMsg.body &&
      !/^\[?(Image|Video|Document|Voice message|Audio|PTT|Sticker)\]?$/i.test(quotedMsg.body) &&
      quotedMsg.body !== "IMAGE" &&
      quotedMsg.body !== "VIDEO" &&
      quotedMsg.body !== "DOCUMENT";

    if (kind === "audio") return "🎤 " + i18n.t("messagesList.quotedAudio");
    if (kind === "sticker") return "🎭 " + i18n.t("messagesList.quotedSticker");
    if (kind === "video") return hasRealCaption ? `🎬 ${quotedMsg.body}` : "🎬 " + i18n.t("messagesList.quotedVideo");
    if (kind === "image") return hasRealCaption ? `📷 ${quotedMsg.body}` : "📷 " + i18n.t("messagesList.quotedImage");
    if (kind === "document") return "📄 " + (hasRealCaption ? quotedMsg.body : i18n.t("messagesList.quotedDocument"));

    return quotedMsg.body;
  };


  const renderQuotedMessage = (message) => {
    // Handle cases where dataJson might be null (e.g., Meta Business API messages)
    let data = null;
    try {
      data = message.quotedMsg?.dataJson ? JSON.parse(message.quotedMsg.dataJson) : null;
    } catch (e) {
      data = null;
    }

    const classification = classifyQuotedMedia(message.quotedMsg, data);
    const { kind, imagePreviewUrl } = classification;
    const showThumb =
      imagePreviewUrl && (kind === "image" || kind === "video" || kind === "sticker");

    // Sender label: "You" if quoting own message, otherwise contact
    // name. Falls back to "" rather than rendering an empty pill
    // when neither is available.
    const senderName = message.quotedMsg?.fromMe
      ? i18n.t("messagesList.replyToYou")
      : message.quotedMsg?.contact?.name || "";

    return (
      <div
        className={clsx(classes.replyPreviewContainer, {
          [classes.replyPreviewContainerOut]: message.fromMe,
        })}
        onClick={() => scrollToMessage(message.quotedMsg.id)}
      >
        <span
          className={clsx(classes.replyPreviewBar, {
            [classes.replyPreviewBarOut]: message.quotedMsg?.fromMe,
          })}
        />
        <div className={classes.replyPreviewText}>
          {senderName && (
            <span className={classes.replyPreviewName}>{senderName}</span>
          )}
          {/* Single-line body: no MarkdownWrapper here — the preview
              is a glance-level summary, not the full message. Markdown
              tokens inside a 1-line ellipsis read as noise. */}
          <span className={classes.replyPreviewBody}>
            {getQuotedMessageText(message.quotedMsg, kind)}
          </span>
        </div>
        {showThumb && (
          <div className={classes.replyPreviewThumb}>
            <img src={imagePreviewUrl} alt={kind} />
            {kind === "video" && (
              <span className={classes.replyPreviewPlay}>
                <PlayArrow />
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  // Security (audit N2/N3): only allow http/https hrefs derived from inbound
  // message content (WhatsApp canonicalUrl / location body) so a crafted
  // "javascript:" payload can't execute in the authenticated CRM origin.
  // Returns undefined for anything else -> React omits the href and the anchor
  // is inert. Legitimate http/https links are unchanged and still open.
  const safeHref = (raw) => {
    if (typeof raw !== "string") return undefined;
    try {
      const u = new URL(raw);
      return u.protocol === "http:" || u.protocol === "https:" ? raw : undefined;
    } catch (_e) {
      return undefined;
    }
  };

  const renderLinkPreview = (message) => {
    // Handle cases where dataJson might be null (e.g., Meta Business API
    // messages). Goes through the shared parse cache — this used to re-parse
    // the same dataJson renderMessages had just parsed for the same bubble.
    const data = parseDataJson(message);

    const title = data?.message?.extendedTextMessage?.title;
    const description = data?.message?.extendedTextMessage?.description;
    const canonicalUrl = data?.message?.extendedTextMessage?.canonicalUrl;
    const url = canonicalUrl && new URL(
      canonicalUrl,
    );
    
    if (!title && !description && !url) {
      return (<></>);
    }
    
    const thumbnail = data?.message?.extendedTextMessage?.jpegThumbnail;
    const imageUrl = thumbnail ? "data:image/png;base64, " + thumbnail : "";
    return (
      <a href={safeHref(canonicalUrl)} rel="noopener noreferrer" className={classes.linkPreviewAnchor} target="_blank">
        <div
          className={clsx(classes.quotedContainerLeft, {
            [classes.quotedContainerRight]: message.fromMe,
          })}
        >
          <div className={classes.quotedMsg}>
            {title &&
              <div className={classes.linkPreviewTitle}>
                {title}
              </div>
            }
            {description &&
              <div className={classes.linkPreviewDescription}>
                {description}
              </div>
            }
            {url?.hostname &&
              <div className={classes.linkPreviewUrl}>
                {url.hostname}
              </div>
            }
          </div>
          {!message.thumbnailUrl && imageUrl && (
            <img className={classes.quotedThumbnail} src={imageUrl} />
          )}
        </div>
      </a>
    );
  };

  const formatVCardN = (n) => {
    return(
      (n[3] ? n[3] + " " : "") +
      (n[1] ? n[1] + " " : "") +
      (n[2] ? n[2] + " " : "") +
      (n[0] ? n[0] + " " : "") +
      (n[4] ? n[4] + " " : "")
    );
  }

  const isVCard = (message) => {
    return message.startsWith('{"godealvCard":');
  };
  
  const stringOrFirstElement = (data) => {
    if (!data) {
      return "";
    }
    if (Array.isArray(data)) {
      return data[0];
    }
    return data;
  };

  const renderVCard = (vcardJson) => {
    const cardArray = JSON.parse(vcardJson)?.godealvCard;
    
    if (!cardArray || !Array.isArray(cardArray)) {
      return <div>{i18n.t("messagesList.invalidVCard") || "Invalid VCARD data"}</div>;
    }

    return cardArray.map((item, idx) => {
      const message = item?.vcard;
      if (!message) {
        return <React.Fragment key={idx}></React.Fragment>;
      }
      const parsedVCard = vCard.parse(message);
      
      const name = stringOrFirstElement(
        parsedVCard['X-WA-BIZ-NAME']?.[0]?.value ||
        parsedVCard.fn?.[0]?.value ||
        formatVCardN(parsedVCard.n?.[0]?.value));
      const description = stringOrFirstElement(
        parsedVCard['X-WA-BIZ-DESCRIPTION']?.[0]?.value || "");
      const number = stringOrFirstElement(parsedVCard?.tel?.[0]?.value);
      const metaNumber = parsedVCard?.tel?.[0]?.meta?.waid?.[0] || number || "unknown";
      
      return (
        <div key={idx} style={{ 
          minWidth: 250, 
          maxWidth: 320,
          padding: "8px 0"
        }}>
          {/* Contact Card Header */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'center', 
            padding: "8px 12px",
            gap: 12
          }}>
            <Avatar 
              style={{ 
                backgroundColor: generateColor(metaNumber), 
                width: 50, 
                height: 50, 
                color: "white", 
                fontWeight: "bold",
                fontSize: 18,
                flexShrink: 0
              }}
            >
              {getInitials(name)}
            </Avatar>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <Typography
                noWrap
                component="div"
                variant="body1"
                style={{ 
                  fontWeight: 600,
                  color: 'inherit',
                  lineHeight: 1.3
                }}
              >
                {name}
              </Typography>
              {description && (
                <Typography
                  component="div"
                  variant="body2"
                  style={{ 
                    opacity: 0.7,
                    fontSize: 13,
                    lineHeight: 1.3
                  }}
                >
                  {description}
                </Typography>
              )}
              <Typography
                component="div"
                variant="body2"
                style={{ 
                  opacity: 0.7,
                  fontSize: 13,
                  lineHeight: 1.3,
                  direction: 'ltr',
                  unicodeBidi: 'embed'
                }}
              >
                {number}
              </Typography>
            </div>
          </div>
          
          {/* Add to Contacts button */}
          <div style={{
            borderTop: '1px solid rgba(0,0,0,0.1)',
            marginTop: 8,
            paddingTop: 8,
            textAlign: 'center'
          }}>
            <Typography
              component="span"
              variant="body2"
              onClick={async () => {
                try {
                  // Clean phone number - remove all non-digits
                  const cleanNumber = (number || metaNumber || "").replace(/\D/g, '');
                  if (!cleanNumber) {
                    toast.warning(i18n.t("messagesList.noPhoneNumber") || "No phone number available");
                    return;
                  }
                  console.log("Adding contact:", { name, number: cleanNumber });
                  await api.post('/contacts', {
                    name: name || "Contact",
                    number: cleanNumber,
                  });
                  toast.success(i18n.t("messagesList.contactAdded") || "Contact added successfully!");
                } catch (err) {
                  console.error("Error adding contact:", err?.response?.data || err);
                  const errorCode = err?.response?.data?.error;
                  if (errorCode === "ERR_DUPLICATED_CONTACT") {
                    toast.info(i18n.t("messagesList.contactExists") || "This contact already exists in your contacts list.");
                  } else {
                    toastError(err);
                  }
                }
              }}
              style={{ 
                fontWeight: 500, 
                color: '#00a884',
                cursor: 'pointer',
                fontSize: 14
              }}
            >
              {i18n.t("messagesList.addToContacts") || "Add to Contacts"}
            </Typography>
          </div>
        </div>
      )
    });
  };

  const messageLocation = (message, createdAt) => {
    return (
      <div className={[classes.textContentItem, { display: 'flex', padding: 5 }]}>
        <img src={message.split('|')[0]} className={classes.imageLocation} />
        <a
          style={{ fontWeight: '700', color: 'gray' }}
          target="_blank"
          rel="noopener noreferrer"
          href={safeHref(message.split('|')[1])}>Click to open location</a>
        <span className={classes.timestamp}>
          {format(parseISO(createdAt), "HH:mm")}
        </span>
      </div>
    )
  };
  const setReaction = (id, reaction) => {
    dispatch({ type: "UPDATE_REACTION", payload: { messageId: id, reaction } });
  }

  // Stable ctx for MessageBubble — identity never changes (memo works), but
  // every getter reads the latest closure via the ref (no stale handlers).
  const bubbleCtxRef = useRef({});
  bubbleCtxRef.current = {
    classes, isGroup, parseDataJson, startBubbleLongPress, cancelBubbleLongPress, handleBubbleContextMenu, handleOpenQuickReactions, handleQuickReply, handleOpenMessageOptionsMenu, renderDailyTimestamps, renderMessageDivider, renderVCard, renderQuotedMessage, renderLinkPreview, messageLocation, checkMessageMedia, renderMessageAck, SelectMessageCheckbox, isVCard
  };
  const bubbleStableCtx = useRef(null);
  if (!bubbleStableCtx.current) {
    bubbleStableCtx.current = new Proxy({}, { get: (_t, k) => bubbleCtxRef.current[k] });
  }

  const renderMessages = () => {
    const viewMessagesList = messagesList.map((message, index) => (
      <MessageBubble
        key={message.id}
        message={message}
        index={index}
        showSelectMessageCheckbox={showSelectMessageCheckbox}
        ctx={bubbleStableCtx.current}
      />
    ));

    // Interleave voice-call cards into the timeline by timestamp. viewMessagesList
    // (the WhatsApp message render output) is left exactly as-is; we only splice
    // call items between message elements at the right chronological position so
    // a call appears inline like a message. Isolated per item in an ErrorBoundary
    // so a malformed call can never blank the conversation.
    if (!callEvents.length) return viewMessagesList;
    const renderCall = (c) => (
      <ErrorBoundary silent key={`call-${c.id}`}>
        <VoiceCallTimelineItem call={c} ticket={ticket} />
      </ErrorBoundary>
    );
    const callTime = (c) => new Date(c.startedAt || c.createdAt).getTime();
    const sortedCalls = [...callEvents].sort((a, b) => callTime(a) - callTime(b));
    const merged = [];
    let ci = 0;
    for (let i = 0; i < viewMessagesList.length; i++) {
      const msgTime = new Date(messagesList[i].createdAt).getTime();
      while (ci < sortedCalls.length && callTime(sortedCalls[ci]) <= msgTime) {
        merged.push(renderCall(sortedCalls[ci]));
        ci++;
      }
      merged.push(viewMessagesList[i]);
    }
    while (ci < sortedCalls.length) {
      merged.push(renderCall(sortedCalls[ci]));
      ci++;
    }
    return merged;
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
       setReaction={setReaction}
       showSelectCheckBox={showSelectMessageCheckbox}
       setShowSelectCheckbox={setShowSelectMessageCheckbox}
       forwardMessageModalOpen={forwardMessageModalOpen}
       setForwardMessageModalOpen={setForwardMessageModalOpen}
       selectedMessages={selectedMessagesList}
        message={selectedMessage}
        data={selectedMessageData}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        quickReactionAnchor={quickReactionAnchor}
        onCloseQuickReaction={handleCloseQuickReactions}
      />
      <div
        id="messagesList"
        className={clsx(classes.messagesList,
          {
            [classes.textRight]: isRTL === 'he',
          }
        )}
        onScroll={handleScroll}
        ref={scrollRef}
      >
        {messagesList.length > 0
          ? renderMessages()
          : loading
          ? <MessagesListSkeleton />
          : []}
        {contactPresence === "composing" && (
          <div className={classes.messageLeft}>
            <div className={classes.wave}>
              <span className={classes.dot}></span>
              <span className={classes.dot}></span>
              <span className={classes.dot}></span>
            </div>
          </div>
        )}
        {contactPresence === "recording" && (
          <div className={classes.messageLeft}>
            <div className={classes.wavebarsContainer}>
              <div className={clsx(classes.wavebars, classes.wavebar1)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar2)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar3)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar4)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar5)}></div>
            </div>
          </div>
        )}
      </div>
      {/* Loading state: MessagesListSkeleton renders inside the scroll area
          (above) while the FIRST page of a freshly-opened ticket is in
          flight — only when the list is empty, so it never overlays real
          bubbles. With the initial fetch no longer debounced 500ms the
          skeleton window is short (fetch latency only); an empty panel
          under a loading header read as broken on slower connections. */}
      {/* Sprint 8D — scroll-to-bottom FAB. Renders unconditionally
          and toggles via opacity so the button doesn't pop in and
          out of the DOM. handleScroll above sets showScrollDown. */}
      <IconButton
        size="small"
        aria-label="scroll to bottom"
        className={clsx(classes.scrollToBottomFab, {
          [classes.scrollToBottomFabVisible]: showScrollDown,
        })}
        onClick={scrollToBottom}
      >
        <KeyboardArrowDown />
      </IconButton>
    </div>
  );
};

export default MessagesList;
